import React, {useEffect, useState} from 'react';

import onClickFunctionKeys from '../../../../utils/pagination';
import styles from './styles/database.module.css';

import CardList from './CardList';
import DetailBox from './DetailBox';
import Header from './Header';
import TableList from './TableList';
import CreateModal from '../create/CreateModal';
import DeleteModal from '../delete/DeleteModal';
import Pagination from '../../../common/Pagination';
import BoxMessage from '../../../design-system/box/BoxMessage';
import ToggleTab from '../../../design-system/toggle/ToggleTab';
import {ReactComponent as CheckIcon} from '../../../../assets/design-system/check.svg';
import {ReactComponent as DatabaseIcon} from '../../../../assets/design-system/database.svg';
import {ReactComponent as GridIcon} from '../../../../assets/design-system/grid.svg';
import {ReactComponent as ListIcon} from '../../../../assets/design-system/list.svg';
import {ReactComponent as XIcon} from '../../../../assets/design-system/x.svg';

export default function Database({ aliasName, dbType, viewType, pagination, listResponse, listError, connectOriginalResponse, connectOriginalError, listLoading, connectOriginalLoading, handleChange, handleReload, handleConnectOriginalDB }) {
    const [isOpenAddModal, setIsOpenAddModal] = useState(false);
    const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
    const [isOpenSidebar, setIsOpenSidebar] = useState(false);

    // DetailBox에서 사용하는 선택된 DB 정보
    const [selectedItem, setSelectedItem] = useState(null);

    // DeleteModal에서 사용하는 삭제할 DB 아이디
    const [deletedId, setDeletedId] = useState(null);

    // BoxStatus에 성공/실패를 표시하기 위한 상태 관리
    const [connectionStatus, setConnectionStatus] = useState(null);

    // DB 연결(추가) 과정 중 현재 단계 이름(현재 단계에 해당하는 컴포넌트를 보여주기 위함) : type, info, test
    const [currentStepName, setCurrentStepName] = useState('type');

    const initialInfo = {  // DB 연결(추가)에 필요한 상태
        id: null, // 신규와 수정 구분을 위해 필요
        dbType: '',
        dbName: '',
        hostName: '',
        portNumber: '',
        accessId: '',
        dbPassword: '',
        dbFile: null,     // 서버에 전달하는 SQLite 파일
        dbFileName: '',   // 화면에 표시되는 SQLite 파일명
        aliasName: ''
    };

    const [connectionInfo, setConnectionInfo] = useState(initialInfo);

    // 페이지네이션
    const [pageNumbers, setPageNumbers] = useState([]);
    const [prevPageNumberLength, setPrevPageNumberLength] = useState(pageNumbers.length);

    const viewTypeMap = [
	    { name: '리스트', value: 'LIST', icon: <ListIcon className={styles.icon} />, activeIcon: <ListIcon className={styles.iconActive} /> },
	    { name: '카드', value: 'GRID', icon: <GridIcon className={styles.icon} />, activeIcon: <GridIcon className={styles.iconActive} /> }
    ];

    const changeViewType = (value) => {
        if (listLoading) return;

        closeSidebar();
        handleChange({
            target: {
                name: 'viewType',
                value
            }
        });
    };

    const toggleAddModal = () => {
        setIsOpenAddModal(!isOpenAddModal);
        setCurrentStepName('type');
        setConnectionInfo(initialInfo);
    };

    const openDeleteModal = (id) => {
        setDeletedId(id);
        setIsOpenDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setDeletedId(null);
        setIsOpenDeleteModal(false);
    };
    
    const openSidebar = (id) => {
        const targetItem = listResponse?.data?.data?.find(database => database.id === id);
        setSelectedItem(targetItem);
        setIsOpenSidebar(true);
    };

    const closeSidebar = () => {
        setIsOpenSidebar(false);
        setSelectedItem(null);
    };

    const getStatus = (state) => {
        if (state === 'NORMAL') return { status: 'active', text: '정상' };
        if (state === 'ERROR') return { status: 'error', text: '오류' };
    };

    const getConnectionStatus = (id) => {
        if (!connectionStatus || (connectionStatus.id !== id)) return null;

        return connectionStatus;
    };

    const getBoxStatusInfo = (currentConnectionStatus, state) => {
        if (currentConnectionStatus && !currentConnectionStatus.isLoading) {
            return {
                status: currentConnectionStatus.isSuccess ? 'success' : 'error',
                icon: currentConnectionStatus.isSuccess ? <CheckIcon /> : <XIcon />,
                text: currentConnectionStatus.isSuccess ? '성공' : '실패'
            };
        }
        
        return {
            status: getStatus(state).status,
            icon: null,
            text: getStatus(state).text
        };
    };

    const getButtonInfo = (currentConnectionStatus, id) => {
        if (currentConnectionStatus && currentConnectionStatus.isLoading) return { text: '연결 테스트 중...', status: 'loading', handleClick: null };
        if (currentConnectionStatus && currentConnectionStatus.isSuccess) return { text: '연결 성공', status: 'success', handleClick: null };
        if (currentConnectionStatus && !currentConnectionStatus.isSuccess) return { text: '연결 실패', status: 'error', handleClick: null };

        return { 
            text: '연결 테스트', 
            status: 'active', 
            handleClick: () => {
                const targetItem = listResponse?.data?.data?.find(item => item.id === id);
                if (targetItem) {
                    setSelectedItem(targetItem);
                }
                handleConnectOriginalDB(id);
            }
        };
    };

    const validateParams = () => {
        return !!(aliasName || dbType);
    };

    // 페이지네이션
    const requestPagination = (page) => {
        handleChange({ 
            target: {
                name: 'page',
                value: page
            }
        });
    };

    const handleClickPaginationFunctionKeys = (functionKey) => {
        requestPagination(onClickFunctionKeys(functionKey, pageNumbers, pagination.currentPage));
    };

    const initializePagination = () => {
        setPrevPageNumberLength(pageNumbers.length);
        let pages = Array.from({ length: pagination.totalPages }, (_, i) => i + 1);
        setPageNumbers(pages);
    };

    useEffect(() => {
        if (!pagination) return;
        initializePagination();
    }, [pagination]);

     // 연결 테스트 후 상세 정보 업데이트
    useEffect(() => {
        if (listResponse?.data?.data && selectedItem) {
            const updatedItem = listResponse.data.data.find(item => item.id === selectedItem.id);
            
            if (updatedItem && (JSON.stringify(updatedItem) !== JSON.stringify(selectedItem))) {
                setSelectedItem(updatedItem);
            }
        }
    }, [listResponse, selectedItem]);

     // 연결 테스트 결과 처리 : 성공 혹은 실패 표시 3초 후 초기화
    useEffect(() => {
        if (connectOriginalLoading && selectedItem) {
            setConnectionStatus({
                id: selectedItem.id,
                isLoading: true,
                isSuccess: false,
                timestamp: Date.now()
            });
        }

        if (!connectOriginalLoading && (connectOriginalResponse || connectOriginalError) && selectedItem) {
            setConnectionStatus({
                id: selectedItem.id,
                isLoading: false,
                isSuccess: connectOriginalResponse?.state === 'NORMAL', // state가 ERROR이거나 connectOriginalError인 경우 isSuccess는 false
                timestamp: Date.now()
            });
            
            const timer = setTimeout(() => {
                setConnectionStatus(null);
                handleChange({ target: { name: 'connectOriginalResponse', value: null } });
                handleChange({ target: { name: 'connectOriginalError', value: null } });
            }, 3000);
            
            return () => clearTimeout(timer);
        }
    }, [connectOriginalResponse, connectOriginalError, connectOriginalLoading, selectedItem]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <Header
                    aliasName={aliasName}
                    response={listResponse}
                    responseData={listResponse?.data?.data}
                    responseCount={listResponse?.data?.count}
                    error={listError}
                    loading={listLoading}
                    handleChange={handleChange}
                    toggleAddModal={toggleAddModal}
                    validateParams={validateParams}
                />
                <div className={styles.box}>
                    <div className={styles.tab}>
                        <ToggleTab
                            width='157px'
                            optionMap={viewTypeMap}
                            selectedOption={viewType}
                            handleClick={changeViewType}
                        />
                    </div>
                    {!listLoading && listResponse && listResponse.data.data && listResponse.data.data.length > 0 && viewType === 'LIST' &&
                        <TableList
                            selectedId={selectedItem?.id}
                            openSidebar={openSidebar}
                            response={listResponse?.data?.data}
                            setCurrentStepName={setCurrentStepName}
                            setConnectionInfo={setConnectionInfo}
                            toggleAddModal={toggleAddModal}
                            openDeleteModal={openDeleteModal}
                            getConnectionStatus={getConnectionStatus}
                            getBoxStatusInfo={getBoxStatusInfo}
                        /> 
                    }
                    {!listLoading && listResponse && listResponse.data.data && listResponse.data.data.length > 0 &&viewType === 'GRID' &&
                        <CardList
                            response={listResponse?.data?.data}
                            setCurrentStepName={setCurrentStepName}
                            setConnectionInfo={setConnectionInfo}
                            toggleAddModal={toggleAddModal}
                            openDeleteModal={openDeleteModal}
                            getStatus={getStatus}
                            getConnectionStatus={getConnectionStatus}
                            getBoxStatusInfo={getBoxStatusInfo}
                            getButtonInfo={getButtonInfo}
                    /> 
                    }
                    {listLoading && <BoxMessage height='800px' type='loading' />}
                    {!listLoading && listResponse && listResponse.data.data && listResponse.data.data.length === 0 && !validateParams() &&
                        <BoxMessage
                            height='800px'
                            icon={<DatabaseIcon />}
                            message='등록된 DB 없음'
                            subMessage='새로운 DB를 추가해 주세요'
                        />
                    }
                    {!listLoading && listResponse && listResponse.data.data &&listResponse.data.data.length === 0 && validateParams() &&
                        <BoxMessage heigh='800px' type='no-search' />
                    }
                    {!listLoading && listError &&
                        <BoxMessage
                            height='800px'
                            type='error'
                            handleReload={handleReload}
                        />    
                    }
                </div>
                {pagination && 
                    <Pagination
                        pageNumbers={pageNumbers}
                        prevPageNumberLength={prevPageNumberLength}
                        currentPage={pagination.currentPage}
                        handleChangePage={requestPagination}
                        handleClickFunctionKeys={handleClickPaginationFunctionKeys}
                    />
                }
            </div>
            {isOpenAddModal &&
                <CreateModal
                    currentStepName={currentStepName}
                    setCurrentStepName={setCurrentStepName}
                    initialInfo={initialInfo}
                    connectionInfo={connectionInfo}
                    setConnectionInfo={setConnectionInfo}
                    listResponse={listResponse?.data?.data}
                    listLoading={listLoading}
                    handleReload={handleReload}
                    toggleAddModal={toggleAddModal}
                />
            }
            {isOpenDeleteModal &&
                <DeleteModal
                    selectedId={selectedItem?.id}
                    deletedId={deletedId}
                    closeSidebar={closeSidebar}
                    closeDeleteModal={closeDeleteModal}
                    handleReload={handleReload}
                />
            }
            {isOpenSidebar 
                && <DetailBox
                    selectedItem={selectedItem}
                    closeSidebar={closeSidebar}
                    getConnectionStatus={getConnectionStatus}
                    getBoxStatusInfo={getBoxStatusInfo}
                    getButtonInfo={getButtonInfo}
                    handleConnectOriginalDB={handleConnectOriginalDB}
                />
            }
        </div>
    );
}
